import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addWatermarkToPdf } from '../../../../utils/pdfWatermark';

export interface SessionForInvoice {
    id: string;
    type: 'solo' | 'team';
    goal: string;
    duration: number;
    completedAt: string;
    startedAt?: string;
    notes?: string | string[];
    members?: string[];
    music?: string;
    tasks?: { task: string; isCompleted: boolean }[];
    xp?: number;
}

export interface InvoiceFields {
    clientName: string;
    currency: string;
    hourlyCharge: number;
    invoiceNumber?: string;
    invoiceDate?: string;
    senderName?: string;
    senderAddress?: string;
    senderEmail?: string;
    clientAddress?: string;
    /** Override session goal/description on the invoice */
    sessionGoalOverride?: string;
    /** Override tasks list (task names only) on the invoice */
    tasksOverride?: string[];
    /** Override total time in minutes for amount calculation */
    totalMinutesOverride?: number;
    /** Bank details */
    payeeName?: string;
    bankName?: string;
    accountNumber?: string;
    ifscCode?: string;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    INR: '₹',
    JPY: '¥',
    CAD: 'C$',
    AUD: 'A$',
};

const formatCurrency = (amount: number, currency: string, withSymbol = true): string => {
    const formatted = Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const symbol = CURRENCY_SYMBOLS[currency] || currency + ' ';
    if (amount < 0) return `(-) ${withSymbol ? symbol : ''}${formatted}`;
    return withSymbol ? `${symbol}${formatted}` : formatted;
};

export async function generateInvoicePDF(
    session: SessionForInvoice,
    invoiceFields: InvoiceFields
): Promise<{ success: boolean; error?: string }> {
    const {
        clientName,
        currency,
        hourlyCharge,
        senderName,
        senderAddress,
        senderEmail,
        clientAddress,
        sessionGoalOverride,
        tasksOverride,
        totalMinutesOverride,
        payeeName,
        bankName,
        accountNumber,
        ifscCode,
    } = invoiceFields;

    if (!clientName?.trim()) {
        return { success: false, error: 'Please enter client name.' };
    }
    if (hourlyCharge == null || hourlyCharge < 0) {
        return { success: false, error: 'Please enter a valid hourly charge.' };
    }

    const effectiveGoal = sessionGoalOverride?.trim() || session.goal;
    const effectiveTasks: string[] = tasksOverride?.length
        ? tasksOverride
        : (session.tasks ? session.tasks.map((t) => t.task) : []);
    const effectiveDurationMinutes = totalMinutesOverride != null && totalMinutesOverride > 0 ? totalMinutesOverride : session.duration;
    const hours = effectiveDurationMinutes / 60;
    const subTotal = hourlyCharge * hours;
    const total = subTotal;

    const invoiceDate = invoiceFields.invoiceDate
        ? new Date(invoiceFields.invoiceDate)
        : new Date(session.completedAt);
    const invoiceNumber = invoiceFields.invoiceNumber?.trim() || `INV-${session.id.slice(0, 8)}`;
    const fromName = senderName?.trim() || '—';

    const formatDate = (d: Date) => d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

    try {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        const margin = 14;

        const black: [number, number, number] = [0, 0, 0];
        const gray: [number, number, number] = [80, 80, 80];
        // App theme: orange accent for headers
        const orangeHeaderBg: [number, number, number] = [255, 243, 224]; // orange-50
        const orangeAccent: [number, number, number] = [234, 88, 12];   // orange-600
        const orangeBorder: [number, number, number] = [254, 215, 170]; // orange-200

        let y = 20;

        // --- Top Left: Sender ---
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...black);
        doc.text(fromName, margin, y);
        y += 6;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...gray);
        if (senderAddress?.trim()) {
            const addrLines = doc.splitTextToSize(senderAddress.trim(), pageWidth / 2 - margin - 4);
            doc.text(addrLines, margin, y);
            y += addrLines.length * 5 + 2;
        } else {
            y += 8;
        }
        if (senderEmail?.trim()) {
            doc.text(senderEmail.trim(), margin, y);
            y += 6;
        }

        // --- Top Right: Invoice title & number ---
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...orangeAccent);
        doc.text('Invoice', pageWidth - margin, 22, { align: 'right' });
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...gray);
        doc.text(`Invoice# ${invoiceNumber} · ${formatDate(invoiceDate)}`, pageWidth - margin, 32, { align: 'right' });

        y = Math.max(y, 42);

        // --- Section divider ---
        doc.setDrawColor(...orangeBorder);
        doc.setLineWidth(0.3);
        doc.line(margin, y, pageWidth - margin, y);
        y += 10;

        // --- Bill To: Client ---
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...gray);
        doc.text('Bill To', margin, y);
        y += 5;
        doc.setFontSize(10);
        doc.setTextColor(...black);
        doc.text(clientName.trim(), margin, y);
        y += 6;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...gray);
        if (clientAddress?.trim()) {
            const clientAddr = doc.splitTextToSize(clientAddress.trim(), pageWidth / 2 - margin - 4);
            doc.text(clientAddr, margin, y);
            y += clientAddr.length * 5 + 4;
        } else {
            y += 6;
        }

        // --- Subject line (bold) ---
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...black);
        const subjectText = `Subject: ${effectiveGoal || 'Professional services'}`;
        doc.text(subjectText, margin, y);
        y += 10;

        // --- Single table: #, Task, Hours, Rate, Amount (subject already shown above, tasks go in Task column) ---
        const taskContent = effectiveTasks.filter(Boolean).length > 0
            ? effectiveTasks.filter(Boolean).map((t) => `• ${t}`).join('\n')
            : '—';
        const tableData = [
            [
                '1',
                taskContent,
                hours.toFixed(2) + ' hr',
                formatCurrency(hourlyCharge, currency, true),
                formatCurrency(subTotal, currency, true),
            ],
        ];

        const serviceTableWidth = pageWidth - 2 * margin;
        autoTable(doc, {
            startY: y,
            head: [['#', 'Task', 'Hours', 'Rate', 'Amount']],
            body: tableData,
            theme: 'grid',
            headStyles: {
                fontStyle: 'bold',
                fontSize: 9,
                textColor: orangeAccent as [number, number, number],
                fillColor: orangeHeaderBg,
                cellPadding: 4,
            },
            bodyStyles: { fontSize: 9, textColor: black as [number, number, number], cellPadding: 4 },
            columnStyles: {
                0: { cellWidth: 12, halign: 'center' },
                1: { cellWidth: serviceTableWidth - 12 - 28 - 38 - 42, halign: 'left' },
                2: { cellWidth: 28, halign: 'right' },
                3: { cellWidth: 38, halign: 'right' },
                4: { cellWidth: 42, halign: 'right' },
            },
            margin: { left: margin, right: margin },
            tableLineColor: orangeBorder as [number, number, number],
            tableLineWidth: 0.3,
        });

        const tableEndY = (doc as any).lastAutoTable.finalY + 16;

        // --- Bottom: Bank Details ---
        const bottomStartY = tableEndY;

        // Bank Details
        if (payeeName || bankName || accountNumber || ifscCode) {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...orangeAccent);
            doc.text('Bank Details', margin, bottomStartY);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...gray);
            let by = bottomStartY + 6;
            if (payeeName?.trim()) {
                doc.text(`Payee Name: ${payeeName.trim()}`, margin, by);
                by += 5;
            }
            if (bankName?.trim()) {
                doc.text(`Bank: ${bankName.trim()}`, margin, by);
                by += 5;
            }
            if (accountNumber?.trim()) {
                doc.text(`Account Number: ${accountNumber.trim()}`, margin, by);
                by += 5;
            }
            if (ifscCode?.trim()) {
                doc.text(`IFSC Code: ${ifscCode.trim()}`, margin, by);
            }
        }

        // --- Footer ---
        doc.setDrawColor(254, 215, 170); // orange-200
        doc.setLineWidth(0.5);
        doc.line(margin, pageHeight - 18, pageWidth - margin, pageHeight - 18);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...gray);
        doc.text(`www.focuzon.com · Session ${session.id.slice(0, 8)}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

        await addWatermarkToPdf(doc);

        const safeName = clientName.replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 30);
        doc.save(`invoice-${invoiceNumber}-${safeName}.pdf`);
        return { success: true };
    } catch (error) {
        console.error('Error generating invoice PDF:', error);
        return { success: false, error: 'Failed to generate invoice. Please try again.' };
    }
}
